import { useQuery } from "@tanstack/react-query";
import type {
	ColumnVisibilityState,
	SortingState,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/contexts/AuthProvider";
import {
	createTableSettingsKey,
	TABLE_SETTINGS_KEYS,
	userConfigQueryOptions,
	useSetConfigurationValueMutation,
} from "@/services/user-config";

/**
 * Table settings state interface
 */
export interface TableSettings<TFilters = Record<string, unknown>> {
	sorting: SortingState;
	columnOrder: string[];
	columnVisibility: ColumnVisibilityState;
	searchTerm: string;
	filters: TFilters;
	pageSize: number;
}

/**
 * Default table settings
 */
const DEFAULT_PAGE_SIZE = 10;

export const DEFAULT_TABLE_SETTINGS: TableSettings = {
	sorting: [],
	columnOrder: [],
	columnVisibility: {},
	searchTerm: "",
	filters: {},
	pageSize: DEFAULT_PAGE_SIZE,
};

/**
 * Options for useTableSettings hook
 */
export interface UseTableSettingsOptions<TFilters = Record<string, unknown>> {
	/** Unique key for this table (e.g., "admin.organizationUsers") */
	tableKey: string;
	/** Default sorting state */
	defaultSorting?: SortingState;
	/** Default column order */
	defaultColumnOrder?: string[];
	/** Default column visibility */
	defaultColumnVisibility?: ColumnVisibilityState;
	/** Default search term */
	defaultSearchTerm?: string;
	/** Default filters */
	defaultFilters?: TFilters;
	/** Default page size (default: 10) */
	defaultPageSize?: number;
	/** Debounce delay in milliseconds for saving (default: 1000) */
	saveDebounceMs?: number;
	/** Whether to enable persistence (default: true) */
	enabled?: boolean;
}

/**
 * Return type for useTableSettings hook
 */
export interface UseTableSettingsReturn<TFilters = Record<string, unknown>> {
	/** Current settings (loaded from API or defaults) */
	settings: TableSettings<TFilters>;
	/** Whether settings are being loaded */
	isLoading: boolean;
	/** Whether settings have been loaded */
	isLoaded: boolean;
	/** Save sorting state */
	saveSorting: (sorting: SortingState) => void;
	/** Save column order */
	saveColumnOrder: (columnOrder: string[]) => void;
	/** Save column visibility */
	saveColumnVisibility: (columnVisibility: ColumnVisibilityState) => void;
	/** Save search term */
	saveSearchTerm: (searchTerm: string) => void;
	/** Save filters */
	saveFilters: (filters: TFilters) => void;
	/** Save page size */
	savePageSize: (pageSize: number) => void;
	/** Reset sorting to default */
	resetSorting: () => void;
	/** Reset column order to default */
	resetColumnOrder: () => void;
	/** Reset column visibility to default */
	resetColumnVisibility: () => void;
	/** Reset search term to default */
	resetSearchTerm: () => void;
	/** Reset filters to default */
	resetFilters: () => void;
	/** Reset page size to default */
	resetPageSize: () => void;
	/** Reset all settings to defaults */
	resetAll: () => void;
}

/**
 * Hook to manage table settings persistence
 *
 * Loads settings from the user configuration API on mount and provides
 * debounced save functions for each setting type.
 *
 * @example
 * ```tsx
 * const {
 *   settings,
 *   isLoading,
 *   saveSorting,
 *   saveColumnOrder,
 *   saveColumnVisibility,
 *   saveSearchTerm,
 *   saveFilters,
 * } = useTableSettings({
 *   tableKey: "admin.organizationUsers",
 *   defaultSorting: [{ id: "nameEmail", desc: false }],
 *   defaultColumnOrder: ["nameEmail", "organizations", "status.name"],
 *   defaultFilters: { organizations: [], roles: [], status: [] },
 * });
 * ```
 */
export function useTableSettings<TFilters = Record<string, unknown>>({
	tableKey,
	defaultSorting = [],
	defaultColumnOrder = [],
	defaultColumnVisibility = {},
	defaultSearchTerm = "",
	defaultFilters = {} as TFilters,
	defaultPageSize = DEFAULT_PAGE_SIZE,
	saveDebounceMs = 1000,
	enabled = true,
}: UseTableSettingsOptions<TFilters>): UseTableSettingsReturn<TFilters> {
	const { user } = useAuth();
	const userId = user?.id;

	// Mutation for saving settings
	const setConfigMutation = useSetConfigurationValueMutation();

	// Debounce timers for each setting type
	const sortingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const columnOrderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	const columnVisibilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	const searchTermTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const filtersTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pageSizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Query to fetch all user config values
	const configQuery = useQuery({
		...userConfigQueryOptions.values(userId ?? ""),
		enabled: enabled && !!userId,
	});

	// Parse settings from the config values
	const settings = useMemo<TableSettings<TFilters>>(() => {
		if (!configQuery.data) {
			return {
				sorting: defaultSorting,
				columnOrder: defaultColumnOrder,
				columnVisibility: defaultColumnVisibility,
				searchTerm: defaultSearchTerm,
				filters: defaultFilters,
				pageSize: defaultPageSize,
			};
		}

		const getValue = (settingType: string): string | null => {
			const key = createTableSettingsKey(tableKey, settingType as never);
			const config = configQuery.data.find((c) => c.key === key);
			return config?.value ?? null;
		};

		// Parse sorting
		let sorting = defaultSorting;
		const sortingValue = getValue(TABLE_SETTINGS_KEYS.SORTING);
		if (sortingValue) {
			try {
				sorting = JSON.parse(sortingValue) as SortingState;
			} catch {
				// Use default on parse error
			}
		}

		// Parse column order
		let columnOrder = defaultColumnOrder;
		const columnOrderValue = getValue(TABLE_SETTINGS_KEYS.COLUMN_ORDER);
		if (columnOrderValue) {
			try {
				columnOrder = JSON.parse(columnOrderValue) as string[];
			} catch {
				// Use default on parse error
			}
		}

		// Parse column visibility
		let columnVisibility = defaultColumnVisibility;
		const columnVisibilityValue = getValue(
			TABLE_SETTINGS_KEYS.COLUMN_VISIBILITY,
		);
		if (columnVisibilityValue) {
			try {
				columnVisibility = JSON.parse(
					columnVisibilityValue,
				) as ColumnVisibilityState;
			} catch {
				// Use default on parse error
			}
		}

		// Parse search term
		let searchTerm = defaultSearchTerm;
		const searchTermValue = getValue(TABLE_SETTINGS_KEYS.SEARCH_TERM);
		if (searchTermValue) {
			try {
				searchTerm = JSON.parse(searchTermValue) as string;
			} catch {
				// Use default on parse error
			}
		}

		// Parse filters
		let filters = defaultFilters;
		const filtersValue = getValue(TABLE_SETTINGS_KEYS.FILTERS);
		if (filtersValue) {
			try {
				filters = JSON.parse(filtersValue) as TFilters;
			} catch {
				// Use default on parse error
			}
		}

		// Parse page size (must be one of allowed values)
		const allowedPageSizes = [10, 25, 50, 100];
		let pageSize = defaultPageSize;
		const pageSizeValue = getValue(TABLE_SETTINGS_KEYS.PAGE_SIZE);
		if (pageSizeValue) {
			try {
				const parsed = Number(JSON.parse(pageSizeValue));
				if (Number.isFinite(parsed) && allowedPageSizes.includes(parsed)) {
					pageSize = parsed;
				}
			} catch {
				// Use default on parse error
			}
		}

		return {
			sorting,
			columnOrder,
			columnVisibility,
			searchTerm,
			filters,
			pageSize,
		};
	}, [
		configQuery.data,
		tableKey,
		defaultSorting,
		defaultColumnOrder,
		defaultColumnVisibility,
		defaultSearchTerm,
		defaultFilters,
		defaultPageSize,
	]);

	// Cleanup timers on unmount
	useEffect(() => {
		return () => {
			if (sortingTimerRef.current) clearTimeout(sortingTimerRef.current);
			if (columnOrderTimerRef.current)
				clearTimeout(columnOrderTimerRef.current);
			if (columnVisibilityTimerRef.current)
				clearTimeout(columnVisibilityTimerRef.current);
			if (searchTermTimerRef.current) clearTimeout(searchTermTimerRef.current);
			if (filtersTimerRef.current) clearTimeout(filtersTimerRef.current);
			if (pageSizeTimerRef.current) clearTimeout(pageSizeTimerRef.current);
		};
	}, []);

	// Generic save function with debouncing
	const saveWithDebounce = useCallback(
		(
			settingType: string,
			value: unknown,
			timerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
		) => {
			if (!userId || !enabled) return;

			// Clear existing timer
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}

			// Set new timer
			timerRef.current = setTimeout(() => {
				const key = createTableSettingsKey(tableKey, settingType as never);
				setConfigMutation.mutate({
					userId,
					key,
					value: JSON.stringify(value),
				});
			}, saveDebounceMs);
		},
		[userId, enabled, tableKey, saveDebounceMs, setConfigMutation],
	);

	// Save functions for each setting type
	const saveSorting = useCallback(
		(sorting: SortingState) => {
			saveWithDebounce(TABLE_SETTINGS_KEYS.SORTING, sorting, sortingTimerRef);
		},
		[saveWithDebounce],
	);

	const saveColumnOrder = useCallback(
		(columnOrder: string[]) => {
			saveWithDebounce(
				TABLE_SETTINGS_KEYS.COLUMN_ORDER,
				columnOrder,
				columnOrderTimerRef,
			);
		},
		[saveWithDebounce],
	);

	const saveColumnVisibility = useCallback(
		(columnVisibility: ColumnVisibilityState) => {
			saveWithDebounce(
				TABLE_SETTINGS_KEYS.COLUMN_VISIBILITY,
				columnVisibility,
				columnVisibilityTimerRef,
			);
		},
		[saveWithDebounce],
	);

	const saveSearchTerm = useCallback(
		(searchTerm: string) => {
			saveWithDebounce(
				TABLE_SETTINGS_KEYS.SEARCH_TERM,
				searchTerm,
				searchTermTimerRef,
			);
		},
		[saveWithDebounce],
	);

	const saveFilters = useCallback(
		(filters: TFilters) => {
			saveWithDebounce(TABLE_SETTINGS_KEYS.FILTERS, filters, filtersTimerRef);
		},
		[saveWithDebounce],
	);

	const savePageSize = useCallback(
		(pageSize: number) => {
			saveWithDebounce(
				TABLE_SETTINGS_KEYS.PAGE_SIZE,
				pageSize,
				pageSizeTimerRef,
			);
		},
		[saveWithDebounce],
	);

	// Reset functions - save the default value immediately (no debounce)
	const resetSorting = useCallback(() => {
		if (!userId || !enabled) return;
		const key = createTableSettingsKey(
			tableKey,
			TABLE_SETTINGS_KEYS.SORTING as never,
		);
		setConfigMutation.mutate({
			userId,
			key,
			value: JSON.stringify(defaultSorting),
		});
	}, [userId, enabled, tableKey, defaultSorting, setConfigMutation]);

	const resetColumnOrder = useCallback(() => {
		if (!userId || !enabled) return;
		const key = createTableSettingsKey(
			tableKey,
			TABLE_SETTINGS_KEYS.COLUMN_ORDER as never,
		);
		setConfigMutation.mutate({
			userId,
			key,
			value: JSON.stringify(defaultColumnOrder),
		});
	}, [userId, enabled, tableKey, defaultColumnOrder, setConfigMutation]);

	const resetColumnVisibility = useCallback(() => {
		if (!userId || !enabled) return;
		const key = createTableSettingsKey(
			tableKey,
			TABLE_SETTINGS_KEYS.COLUMN_VISIBILITY as never,
		);
		setConfigMutation.mutate({
			userId,
			key,
			value: JSON.stringify(defaultColumnVisibility),
		});
	}, [userId, enabled, tableKey, defaultColumnVisibility, setConfigMutation]);

	const resetSearchTerm = useCallback(() => {
		if (!userId || !enabled) return;
		const key = createTableSettingsKey(
			tableKey,
			TABLE_SETTINGS_KEYS.SEARCH_TERM as never,
		);
		setConfigMutation.mutate({
			userId,
			key,
			value: JSON.stringify(defaultSearchTerm),
		});
	}, [userId, enabled, tableKey, defaultSearchTerm, setConfigMutation]);

	const resetFilters = useCallback(() => {
		if (!userId || !enabled) return;
		const key = createTableSettingsKey(
			tableKey,
			TABLE_SETTINGS_KEYS.FILTERS as never,
		);
		setConfigMutation.mutate({
			userId,
			key,
			value: JSON.stringify(defaultFilters),
		});
	}, [userId, enabled, tableKey, defaultFilters, setConfigMutation]);

	const resetPageSize = useCallback(() => {
		if (!userId || !enabled) return;
		const key = createTableSettingsKey(
			tableKey,
			TABLE_SETTINGS_KEYS.PAGE_SIZE as never,
		);
		setConfigMutation.mutate({
			userId,
			key,
			value: JSON.stringify(defaultPageSize),
		});
	}, [userId, enabled, tableKey, defaultPageSize, setConfigMutation]);

	const resetAll = useCallback(() => {
		resetSorting();
		resetColumnOrder();
		resetColumnVisibility();
		resetSearchTerm();
		resetFilters();
		resetPageSize();
	}, [
		resetSorting,
		resetColumnOrder,
		resetColumnVisibility,
		resetSearchTerm,
		resetFilters,
		resetPageSize,
	]);

	return {
		settings,
		isLoading: configQuery.isLoading,
		isLoaded: configQuery.isSuccess,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		saveFilters,
		savePageSize,
		resetSorting,
		resetColumnOrder,
		resetColumnVisibility,
		resetSearchTerm,
		resetFilters,
		resetPageSize,
		resetAll,
	};
}
